import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  IconButton,
  Pagination,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/system";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
// import { members } from "../../../../data/Data";
// import { cities } from "../../../../data/Data";
// import { expertises } from "../../../../data/Data";
import FemaleProfilePhoto from "../../../../assets/images/female-doc.webp";
import MaleProfilePhoto from "../../../../assets/images/male-doc.png";
import { SelectChangeEvent } from "@mui/material";
import { useNavigate } from "react-router-dom";
import TitleSectionComponent from "../../../../components/core-components/titleSection";
import Tooltip from "@mui/material/Tooltip";
import StarRateRoundedIcon from "@mui/icons-material/StarRateRounded";
import { MemberService } from "../../../../network/MemberService";
import { useQuery } from "@tanstack/react-query";
import { CitiesService } from "../../../../network/CitiesService";
import { DepartmentsService } from "../../../../network/DepartmentsService";

interface Member {
  _id: string;
  memberNumber?: string;
  name: string;
  surname: string;
  title?: string;
  department?: string;
  city?: string;
  customCity?: string;
  gender?: string;
  image?: string;
  institutions: {
    name: string;
    address: string;
  }[];
  socialMedia?: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
    instagram?: string;
    youtube?: string;
  };
  boards?: {
    management?: {
      isSelected: boolean;
      position?: string;
      role?: string;
    };
    disciplinary?: {
      isSelected: boolean;
      position?: string;
      role?: string;
    };
    audit?: {
      isSelected: boolean;
      position?: string;
      role?: string;
    };
  };
  isBJKCongressMember?: boolean;
}

const getBoardRole = (member: any) => {
  try {
    if (member?.boards?.management?.isSelected) {
      const role = member.boards.management.role === "Main Member" ? "Asıl Üye" : "Yedek Üye";
      const position = member.boards.management.position ? `Yönetim Kurulu ${member.boards.management.position} - ` : '';
      return `${position}${role}`;
    }
    if (member?.boards?.disciplinary?.isSelected) {
      const role = member.boards.disciplinary.role === "Main Member" ? "Asıl Üye" : "Yedek Üye";
      const position = member.boards.disciplinary.position ? `Disiplin Kurulu ${member.boards.disciplinary.position} - ` : '';
      return `${position}${role}`;
    }
    if (member?.boards?.audit?.isSelected) {
      const role = member.boards.audit.role === "Main Member" ? "Asıl Üye" : "Yedek Üye";
      const position = member.boards.audit.position ? `Denetim Kurulu ${member.boards.audit.position} - ` : '';
      return `${position}${role}`;
    }
    return "";
  } catch (error) {
    console.error("Error getting board role:", error);
    return "";
  }
};

const DoctorCard = styled(Card)(({ theme }) => ({
  textAlign: "center",
  borderRadius: "16px",
  boxShadow: "0px 4px 4px 4px rgba(0, 0, 0, 0.2)",
  transition: "transform 0.3s, box-shadow 0.3s",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: "0px 4px 4px 4px rgba(0, 0, 0, 0.2)",
  },
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
}));

const CityBox = styled(Box)(({ theme }) => ({
  backgroundColor: "var(--red-primary)",
  color: "#fff",
  borderRadius: "8px",
  padding: theme.spacing(1),
  top: "0",
}));

const MembersPerPage = 24;

const turkishCharMap: Record<string, string> = {
  'ç': 'c', 'Ç': 'c',
  'ğ': 'g', 'Ğ': 'g',
  'ı': 'i', 'I': 'i',
  'i': 'i', 'İ': 'i',
  'ö': 'o', 'Ö': 'o',
  'ş': 's', 'Ş': 's',
  'ü': 'u', 'Ü': 'u'
};

const normalizeText = (text: string): string => {
  if (!text) return '';
  
  const preProcessed = text
    .replace(/İ/g, 'i')
    .replace(/I/g, 'ı')
    .toLowerCase();
  
  return preProcessed.replace(/[çğıiöşü]/g, char => turkishCharMap[char] || char);
};

const Members = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = useState(1);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedExpertises, setSelectedExpertises] = useState<string[]>([]);
  // const memberCities = new Set(members.map((member) => member.city));
  // const availableCities = cities.filter((city) => memberCities.has(city.name));
  // const memberExpertises = new Set(members.map((member) => member.expertise));
  // const availableExpertises = expertises
  //   .filter((expertise) => memberExpertises.has(expertise.name))
  //   .sort((a, b) => a.name.localeCompare(b.name));

  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const memberService = new MemberService();

  const { data: membersList, isLoading } = useQuery({
    queryKey: ["members"],
    queryFn: async () => {
      const res = await memberService.getAll();
      // Sort by member number
      return res?.data?.sort((a: Member, b: Member) => {
        const aNumber = a.memberNumber ? parseInt(a.memberNumber) : Infinity;
        const bNumber = b.memberNumber ? parseInt(b.memberNumber) : Infinity;
        return aNumber - bNumber;
      });
    }
  });

  const citiesService = new CitiesService();

  const { data: citiesList = [] } = useQuery({
    queryKey: ["cities"],
    queryFn: async () => {
      const res = await citiesService.getAll();
      return res?.data || [];
    },
  });

  const departmentsService = new DepartmentsService();

  const { data: departmentsList = [] } = useQuery({
    queryKey: ["departments"],
    queryFn: async () => {
      const res = await departmentsService.getAll();
      return res?.data || [];
    },
  });

  const handleCityChange = (
    event: SelectChangeEvent<typeof selectedCities>
  ) => {
    const {
      target: { value },
    } = event;
    setSelectedCities(typeof value === "string" ? value.split(",") : value);
  };

  const handleExpertiseChange = (
    event: SelectChangeEvent<typeof selectedExpertises>
  ) => {
    const {
      target: { value },
    } = event;
    setSelectedExpertises(typeof value === "string" ? value.split(",") : value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const filteredMembers = membersList
    ?.filter(
      (member: { city: string }) =>
        selectedCities.length === 0 ||
        selectedCities.includes(member.city ?? "")
    )
    .filter(
      (member: { department: string }) =>
        selectedExpertises.length === 0 ||
        selectedExpertises.includes(member.department ?? "")
    )
    .filter((member: { 
      name: string; 
      surname: string; 
      department: string; 
      city: string;
      customCity: string;
    }) => {
      const searchTermNormalized = normalizeText(searchTerm);
      
      const fullName = normalizeText(`${member.name} ${member.surname}`);
      const department = normalizeText(member.department || '');
      const city = normalizeText(member.customCity || member.city || '');
    
      return fullName.includes(searchTermNormalized) || 
             department.includes(searchTermNormalized) ||
             city.includes(searchTermNormalized);
    });

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const paginatedMembers = filteredMembers?.slice(
    (page - 1) * MembersPerPage,
    page * MembersPerPage
  );

  return (
    <section
      id="about"
      style={{
        backgroundColor: "var(--bg-color)",
        height: "100%",
        paddingBottom: "100px",
      }}
    >
      <div className="container">
        <TitleSectionComponent titleText="Beşiktaşlı Tıbbiyeliler" />

        <Grid container spacing={4} mb={isMobile ? 4 : 0}>
          <Grid item xs={isMobile ? 12 : 4} my={isMobile ? 0 : 4}>
            <Select
              multiple
              value={selectedCities}
              onChange={handleCityChange}
              displayEmpty
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>Tüm Şehirler</em>;
                }
                return selected.join(", ");
              }}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "black",
                  },
                },
                "& .MuiMenuItem-root": {
                  "&:hover": {
                    backgroundColor: "grey",
                  },
                  "&.Mui-selected, &.Mui-selected:hover": {
                    backgroundColor: "grey",
                  },
                },
              }}
            >
                      {citiesList
            ?.filter((city: { name: string }) => {
              return membersList?.some((member: { city: string }) => {
                return member.city === city.name;
              });
            })

                .map((city: { _id: string; name: string }) => (
                  <MenuItem key={city._id} value={city.name}>
                    <Checkbox
                      checked={selectedCities.indexOf(city.name) > -1}
                    />
                    <ListItemText primary={city.name} />
                  </MenuItem>
                ))}
            </Select>
          </Grid>
          <Grid item xs={isMobile ? 12 : 4} my={isMobile ? 0 : 4}>
            <Select
              multiple
              value={selectedExpertises}
              onChange={handleExpertiseChange}
              displayEmpty
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>Tüm Branşlar</em>;
                }
                return selected.join(", ");
              }}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "black",
                  },
                },
                "& .MuiMenuItem-root": {
                  "&:hover": {
                    backgroundColor: "grey",
                  },
                  "&.Mui-selected, &.Mui-selected:hover": {
                    backgroundColor: "grey",
                  },
                },
              }}
            >
              {departmentsList?.map(
                (expertise: { _id: string; name: string }) => (
                  <MenuItem key={expertise._id} value={expertise.name}>
                    <Checkbox
                      checked={selectedExpertises.indexOf(expertise.name) > -1}
                    />
                    <ListItemText primary={expertise.name} />
                  </MenuItem>
                )
              )}
            </Select>
          </Grid>
          <Grid item xs={isMobile ? 12 : 4} my={isMobile ? 0 : 4}>
            <TextField
              fullWidth
              label="İsim, branş veya şehir ara"
              variant="outlined"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "black",
                  },
                },
                "& .MuiInputLabel-root": {
                  "&.Mui-focused": {
                    color: "#000",
                  },
                },
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={4}>
          {paginatedMembers?.map((member: any, index: number) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <DoctorCard>
                <Box position="relative">
                  <img
                    onClick={() =>
                      navigate(
                        `/uyeler/${member._id}/${member.name}-${member.surname}`
                      )
                    }
                    alt={member.name}
                    src={
                      member.image ||
                      (member.gender?.toLowerCase() === "male"
                        ? MaleProfilePhoto
                        : FemaleProfilePhoto)
                    }
                    style={{
                      width: "100%",
                      height: "300px",
                      borderRadius: "16px 16px 0 0",
                      cursor: "pointer",
                      objectFit: "cover",
                    }}
                  />
                  {member.isBJKCongressMember === true && (
                    <Box
                      position="absolute"
                      top={8}
                      right={8}
                      bgcolor="rgba(255, 255, 255, 0.8)"
                      borderRadius="50%"
                      padding="4px"
                    >
                      <Tooltip title={"BJK Kongre Üyesi"}>
                        <StarRateRoundedIcon style={{ color: "silver" }} />
                      </Tooltip>
                    </Box>
                  )}

                  <CityBox>
                    <span
                      className="B-18"
                      style={{ color: "var(--white-color)" }}
                    >
                      { `${member.customCity || member.city} - ${member.department}`}
                    </span>
                  </CityBox>
                </Box>

                <CardContent>
                  <Stack spacing={1}>
                    <Box
                      onClick={() =>
                        navigate(`/uyeler/${member._id}/${member.name}`)
                      }
                      sx={{ cursor: "pointer" }}
                    >
                      <span className="H-24">
                        {member.title} {member.name} {member.surname}
                      </span>
                    </Box>

                    <span className="B-18">
                      {React.Children.toArray(
                        member.institutions.map(
                          (institution: { name: string; address: string }) =>
                            institution.name
                        )
                      ).join(" / ")}
                    </span>
                  </Stack>
                </CardContent>
                <Box mb={2}>
                  {member.socialMedia?.linkedin && (
                    <IconButton
                      component="a"
                      href={member.socialMedia.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <LinkedInIcon />
                    </IconButton>
                  )}

                  {member.socialMedia?.twitter && (
                    <IconButton
                      component="a"
                      href={member.socialMedia.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <TwitterIcon />
                    </IconButton>
                  )}

                  {member.socialMedia?.facebook && (
                    <IconButton
                      component="a"
                      href={member.socialMedia.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FacebookIcon />
                    </IconButton>
                  )}

                  {member.socialMedia?.instagram && (
                    <IconButton
                      component="a"
                      href={member.socialMedia.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <InstagramIcon />
                    </IconButton>
                  )}

                  {member.socialMedia?.youtube && (
                    <IconButton
                      component="a"
                      href={member.socialMedia.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <YouTubeIcon />
                    </IconButton>
                  )}
                </Box>
              </DoctorCard>
            </Grid>
          ))}
        </Grid>
        <Box mt={4} display="flex" justifyContent="center">
          <Pagination
            count={Math.ceil(filteredMembers?.length / MembersPerPage)}
            page={page}
            onChange={handleChangePage}
            variant="outlined"
            sx={{
              "& .MuiPaginationItem-root": {
                color: "var(--red-primary)",
                width: "40px",
                height: "40px",
              },
              "& .MuiPaginationItem-page.Mui-selected": {
                backgroundColor: "var(--red-secondary)",
                color: "#fff",
              },
              "& .MuiPaginationItem-page:hover": {
                backgroundColor: "var(--red-primary)",
                color: "#fff",
              },
            }}
          />
        </Box>
      </div>
    </section>
  );
};

export default Members;
